import { CWILL_EVENT_NAMES } from "@channelwill/report"
import { useSmartVisibleCrossSellTracker } from "@packages/polling-checker/index"
import { ActionList, Button, Card, Popover } from "@shopify/polaris"
import { AppsIcon, MenuHorizontalIcon, XIcon } from "@shopify/polaris-icons"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RecommendAppI } from "@/api/common/types/RecommendApps"
import CardTitle from "@/component/TitleGroup/CardTitle"
import { BannerEnum } from "@/models/IUserInfo"
import stores from "@/stores"
import MonorailTracker from "@/utils/monorailTracker"

import APPCard from "./APPCard"
import styles from "./index.module.scss"

const RecommendedCard = ({
  recommendedApps,
}: {recommendedApps: RecommendAppI[]}) => {
  const { t } = useTranslation("dashboard")
  const navigator = useNavigate()

  const [popoverActive, setPopoverActive] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    [],
  )

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  const handleDismiss = useCallback(() => {
    setPopoverActive(false)
    // 添加你的 dismiss 逻辑
    console.log("Dismiss action triggered")
    stores.userInfoSore.closeBannerById(BannerEnum.CARD_DISMISSED_UNLOCK_TOP_APPS)
  }, [])

  const handleViewMoreApps = useCallback(() => {
    setPopoverActive(false)
    navigator({ pathname: "/recommend-apps" })
  }, [navigator])

  // 动态生成 popover 菜单项
  const getPopoverItems = useCallback(() => {
    const items = []

    // 移动端时添加 ViewMoreApps 选项
    if (isMobile) {
      items.push({
        content: t("ViewMoreApps"),
        icon: AppsIcon,
        onAction: handleViewMoreApps,
      })
    }

    items.push({
      content: t("dismiss"),
      icon: XIcon,
      onAction: handleDismiss,
    })

    return items
  }, [isMobile, t, handleViewMoreApps, handleDismiss])

  // 可见性监听 - 当元素可见时触发
  const { ref: toRef } = useSmartVisibleCrossSellTracker({
    onViewed: () => {
      recommendedApps.map((app, index) => {
        setTimeout(() => {
          MonorailTracker.trackRecommendAppViewed(app.id)

          window.cwreport && window.cwreport.report(CWILL_EVENT_NAMES.RECOMMEND_APP_EXPOSURE, {
            position: "home_recommended_apps",
            /** 推荐app id */
            recommend_app_id: app.id ? String(app.id) : "",
            /** 推荐app名称 */
            recommend_app_name: app.name,
          })
        }, (index + 1) * 100)
      })
    },
  })

  return (
    <div className={styles.cardContainer}>
      <Card>
        <CardTitle
          title={t("UnlockApps")}
          titleRight={
            <div className={styles.titleActions}>
              <div className={styles.viewMoreButton}>
                <Button
                  onClick={handleViewMoreApps}
                  variant={"plain"}
                >
                  {t("ViewMoreApps")}
                </Button>
              </div>
              <Popover
                active={popoverActive}
                activator={
                  <div className={"flex"}>
                    <Button
                      variant={"tertiary"}
                      icon={MenuHorizontalIcon}
                      onClick={togglePopoverActive}
                    />
                  </div>
                }
                onClose={togglePopoverActive}
              >
                <ActionList
                  actionRole="menuitem"
                  items={getPopoverItems()}
                />
              </Popover>
            </div>
          }
        />
        <div className={styles.appsGrid} ref={toRef as React.RefObject<HTMLDivElement>}>
          {recommendedApps.map((app) => (
            <APPCard
              key={app.id}
              id={app.id}
              logo={app.img_url}
              title={app.name}
              description={app.description}
              link={app.url}
              badges={app.badges}
              hasHover
              position="home_recommended_apps"
            />
          ))}
        </div>
      </Card>
    </div>
  )
}

export { RecommendedCard }
